import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Toaster, toast } from "sonner";
import {
  ArrowRight, ArrowLeft, ShieldCheck, Lightning, Star, CheckCircle, Phone,
  CaretRight, CaretDown,
} from "@phosphor-icons/react";

import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import RelatedGuides from "@/components/landing/RelatedGuides";
import { SERVICES } from "@/data/services-config";
import seoRoutes from "@/data/seo-routes.json";
import { trackLeadConversion } from "@/lib/analytics";
import { useBooking } from "@/contexts/BookingContext";
import { emitChatEvent } from "@/lib/chatBus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_ORIGIN = "https://techconnectncllc.services";

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      data-testid={`service-faq-${idx}`}
      className="border border-zinc-200 rounded-2xl bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`service-faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-zinc-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-zinc-900 text-[15px] md:text-base">{q}</span>
        <CaretDown
          size={18}
          weight="bold"
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-6 -mt-1 text-zinc-600 leading-relaxed text-[15px]">{a}</div>
      )}
    </div>
  );
}

function InlineLeadForm({ service, keyword }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const [first, ...rest] = form.name.trim().split(/\s+/);
      const payload = {
        firstName: first,
        lastName: rest.join(" "),
        email: form.email.trim(),
        phone: form.phone,
        service,
        message: form.message,
        source: `landing-page:${keyword}`,
      };
      await axios.post(`${API}/leads`, payload);
      trackLeadConversion({ service, event_source: `landing-page:${keyword}` });
      setDone(true);
      toast.success("Thanks! We'll be in touch within 1 business hour.");
    } catch (err) {
      toast.error("Could not submit — please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="service-lead-success"
        className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-3"
      >
        <CheckCircle size={22} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-emerald-900">Got it — we&apos;ll be in touch.</div>
          <div className="text-sm text-emerald-800 mt-1">
            Expect a call or text within 1 business hour. For emergencies, call (919) 737-5442.
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="service-lead-form"
      className="space-y-3 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid="service-lead-name"
          required
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Full name"
          className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          data-testid="service-lead-phone"
          required
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="Phone"
          type="tel"
          className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <input
        data-testid="service-lead-email"
        required
        value={form.email}
        onChange={(e) => update("email", e.target.value)}
        placeholder="Email"
        type="email"
        className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        data-testid="service-lead-message"
        value={form.message}
        onChange={(e) => update("message", e.target.value)}
        placeholder={`Tell us about your ${keyword} needs (optional)`}
        rows={3}
        className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid="service-lead-submit"
        className="group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-3.5 font-semibold transition-all"
      >
        {submitting ? "Sending…" : "Request a Callback"}
        <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
      </button>
      <p className="text-xs text-zinc-500 leading-relaxed">
        By submitting, you agree to be contacted by TechConnect about your request. No spam.
      </p>
    </form>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const { business, openBooking } = useBooking();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  // Proactive chat opener after 20s of dwelling on this service page
  useEffect(() => {
    const data = SERVICES[slug];
    if (!data) return undefined;
    const t = setTimeout(() => {
      // Only fire if user is still on the page (not hidden tab)
      if (document.visibilityState !== "visible") return;
      emitChatEvent({
        type: "proactive",
        text: `Looking at our ${data.keyword || "services"}? I can answer questions or help you book a same-day spot — just say the word.`,
        openerForChat: `Hey! I noticed you're checking out our ${data.keyword || data.h1.toLowerCase()}. Want me to help you book a same-day appointment, share pricing, or answer a quick question?`,
      });
    }, 20000);
    return () => clearTimeout(t);
  }, [slug]);

  const data = SERVICES[slug];
  if (!data) return <Navigate to="/" replace />;

  const openBookingFor = (svc) => {
    openBooking(svc || "");
  };

  const handlePrimary = () => {
    const cta = data.primaryCTA || {};
    if (cta.booking) return openBookingFor(cta.booking);
    if (cta.scrollsTo === "contact") {
      const el = document.getElementById("service-contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (cta.tel) {
      window.location.href = `tel:${cta.tel}`;
    }
  };

  const handleSecondary = () => {
    const cta = data.secondaryCTA || {};
    if (cta.booking) return openBookingFor(cta.booking);
    if (cta.tel) {
      window.location.href = `tel:${cta.tel}`;
      return;
    }
    if (cta.scrollsTo === "contact") {
      const el = document.getElementById("service-contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const canonical = `${SITE_ORIGIN}/${slug}`;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  const seoOverride = seoRoutes[`/${slug}`] || {};
  const seoTitle = seoOverride.title || data.title;
  const seoDescription = seoOverride.description || data.metaDescription;

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.h1,
    description: seoDescription,
    provider: { "@id": `${SITE_ORIGIN}/#org` },
    areaServed: [
      { "@type": "State", name: "North Carolina" },
      { "@type": "City", name: "Clayton, NC" },
      { "@type": "City", name: "Granite Falls, NC" },
      ...(slug === "remote-it-support" || slug === "managed-it-services"
        ? [{ "@type": "Country", name: "United States" }]
        : []),
    ],
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
      { "@type": "ListItem", position: 2, name: data.h1, item: canonical },
    ],
  };

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <Header business={business} topOffset={0} onBook={() => openBooking("")} />

      <main>
        {/* Breadcrumb */}
        <div className="bg-zinc-50 border-b border-zinc-200 pt-28 md:pt-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm text-zinc-600">
            <Link to="/" data-testid="breadcrumb-home" className="hover:text-blue-600 inline-flex items-center gap-1">
              <ArrowLeft size={14} weight="bold" />
              Home
            </Link>
            <CaretRight size={12} weight="bold" className="text-zinc-400" />
            <span className="text-zinc-900 font-medium">{data.heroEyebrow}</span>
          </div>
        </div>

        {/* HERO */}
        <section
          data-testid="service-hero"
          className="relative bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white overflow-hidden"
        >
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.18),transparent_55%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs tracking-widest uppercase text-blue-300">
                <ShieldCheck size={14} weight="fill" /> {data.heroEyebrow}
              </div>
              <h1
                data-testid="service-h1"
                className="mt-6 font-heading font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {data.h1}
                {data.h1Accent && <span className="block text-blue-500">{data.h1Accent}</span>}
              </h1>
              <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">{data.subhead}</p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={handlePrimary}
                  data-testid="service-cta-primary"
                  className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 font-semibold shadow-lg shadow-blue-600/30 transition-all"
                >
                  {data.primaryCTA?.label || "Book Now"}
                  <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  onClick={handleSecondary}
                  data-testid="service-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-6 py-3.5 font-semibold transition-all"
                >
                  {data.secondaryCTA?.label || "Contact Us"}
                </button>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-400">
                <span className="inline-flex items-center gap-1.5"><Lightning size={14} weight="duotone" className="text-blue-400" /> Same-day options</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="duotone" className="text-blue-400" /> No-fix-no-fee</span>
                <span className="inline-flex items-center gap-1.5"><Phone size={14} weight="duotone" className="text-blue-400" /> Serving all of NC</span>
              </div>
            </div>

            {/* Floating side card */}
            <div className="lg:col-span-2">
              <div className="relative bg-white text-zinc-900 rounded-2xl p-7 shadow-2xl shadow-blue-600/20 border border-white/10">
                <div className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                  Free Consultation
                </div>
                <div className="mt-2 font-heading text-2xl font-extrabold">
                  Talk to a real NC technician.
                </div>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                  We respond within 1 business hour. No bots, no overseas call center.
                </p>
                <div className="mt-5">
                  <InlineLeadForm service={data.h1} keyword={data.keyword} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section data-testid="service-overview" className="bg-white py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              Overview
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              Why this matters
            </h2>
            <div className="mt-6 space-y-5 text-lg text-zinc-700 leading-relaxed prose-inline-links">
              {data.overview.map((p, i) => (
                <p
                  key={i}
                  data-testid={`service-overview-p-${i}`}
                  dangerouslySetInnerHTML={{ __html: p }}
                />
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section data-testid="service-benefits" className="bg-zinc-50 py-16 md:py-24 border-y border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
                What&apos;s included
              </div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                Everything you get — no extras to chase.
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.benefits.map((b, i) => (
                <article
                  key={i}
                  data-testid={`service-benefit-${i}`}
                  className="group bg-white border border-zinc-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl hover:border-blue-300 transition-all duration-300"
                >
                  <div className="h-11 w-11 rounded-xl bg-blue-600/10 text-blue-600 grid place-items-center">
                    <CheckCircle size={22} weight="duotone" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-zinc-900">
                    {b.href ? (
                      <Link
                        to={b.href}
                        data-testid={`service-benefit-${i}-link`}
                        className="text-zinc-900 hover:text-blue-600 underline decoration-blue-200 underline-offset-4 hover:decoration-blue-500 transition-colors"
                      >
                        {b.title}
                      </Link>
                    ) : (
                      b.title
                    )}
                  </h3>
                  <p className="mt-2 text-zinc-600 leading-relaxed text-[15px]">{b.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section data-testid="service-process" className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
                How it works
              </div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                Simple, transparent, no surprises.
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.process.map((step, i) => (
                <div
                  key={i}
                  data-testid={`service-process-${i}`}
                  className="relative bg-zinc-50 border border-zinc-200 rounded-2xl p-6"
                >
                  <div className="text-blue-600 font-heading font-extrabold text-2xl">{step.step}</div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-zinc-900">{step.title}</h3>
                  <p className="mt-2 text-zinc-600 leading-relaxed text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DISCLAIMER (optional) */}
        {data.disclaimer && (
          <section data-testid="service-disclaimer" className="bg-amber-50 border-y border-amber-200 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-sm font-semibold uppercase tracking-widest text-amber-700">
                Honest expectations
              </div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                {data.disclaimer.title}
              </h2>
              <p
                className="mt-6 text-lg text-zinc-800 leading-relaxed prose-inline-links"
                data-testid="service-disclaimer-body"
                dangerouslySetInnerHTML={{ __html: data.disclaimer.body }}
              />
            </div>
          </section>
        )}

        {/* PRICING (optional) */}
        {data.pricing && (
          <section data-testid="service-pricing" className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16 md:py-20 border-y border-blue-100">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
                Transparent pricing
              </div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                {data.pricing.label}
              </h2>
              <div className="mt-4 inline-flex items-baseline gap-1">
                <span className="text-5xl font-heading font-extrabold text-zinc-900">${data.pricing.amount}</span>
              </div>
              <p className="mt-3 text-zinc-600 max-w-xl mx-auto">{data.pricing.note}</p>
              <button
                onClick={handlePrimary}
                data-testid="service-pricing-cta"
                className="mt-7 group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 font-semibold shadow-lg shadow-blue-600/30 transition-all"
              >
                {data.primaryCTA?.label || "Book Now"}
                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </section>
        )}

        {/* FAQS */}
        <section data-testid="service-faqs" className="bg-white py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              FAQs
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              Questions we hear a lot.
            </h2>
            <p className="mt-3 text-zinc-600">If you don&apos;t see your question here, just call or text us.</p>

            <div className="mt-10 space-y-3">
              {data.faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} idx={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Related guides (blog cross-linking) */}
        <RelatedGuides slug={slug} />

        {/* FINAL CTA */}
        <section
          id="service-contact"
          data-testid="service-final-cta"
          className="relative bg-zinc-950 text-white overflow-hidden py-16 md:py-24"
        >
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(37,99,235,0.2),transparent_55%)]" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                Ready to get started?
              </h2>
              <p className="mt-4 text-lg text-zinc-300 leading-relaxed">
                Talk to a real North Carolina technician — usually within 1 business hour. No bots, no overseas
                call centers, no obligation.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={handlePrimary}
                  data-testid="service-finalcta-primary"
                  className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 font-semibold shadow-lg shadow-blue-600/30 transition-all"
                >
                  {data.primaryCTA?.label || "Book Now"}
                  <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="tel:+19197375442"
                  data-testid="service-finalcta-call"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-6 py-3.5 font-semibold transition-all"
                >
                  <Phone size={18} weight="bold" />
                  Call (919) 737-5442
                </a>
              </div>
            </div>
            <div>
              <InlineLeadForm service={data.h1} keyword={data.keyword} />
            </div>
          </div>
        </section>
      </main>

      <Footer business={business} onBook={() => openBooking("")} />
      <Toaster richColors position="top-right" closeButton />
    </div>
  );
}
